import type {
  CreateIntentRequest,
  CreateIntentResponse,
  IntentFileRef,
} from "./types";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  "https://app.docsumo.com/api/v1/cubone";
const MOCK_API = import.meta.env.VITE_MOCK_API === "true";

const BASIC_AUTH_USER = import.meta.env.VITE_BASIC_AUTH_USER || "";
const BASIC_AUTH_PASS = import.meta.env.VITE_BASIC_AUTH_PASS || "";
const BASIC_AUTH_HEADER =
  BASIC_AUTH_USER && BASIC_AUTH_PASS
    ? `Basic ${btoa(`${BASIC_AUTH_USER}:${BASIC_AUTH_PASS}`)}`
    : null;

type SuccessResponse<T> = {
  data: T;
  message: string;
  status_code: number;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // For multipart bodies (FormData) the browser must set Content-Type itself
  // so the boundary parameter gets included. We only force JSON content-type
  // when the caller didn't pass FormData.
  const isFormBody =
    typeof FormData !== "undefined" && init?.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormBody ? {} : { "Content-Type": "application/json" }),
    ...((init?.headers as Record<string, string>) || {}),
  };
  if (BASIC_AUTH_HEADER) {
    headers["Authorization"] = BASIC_AUTH_HEADER;
  }
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
  });
  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    try {
      const body = await res.json();
      message = body?.message || body?.error || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  const body = (await res.json()) as SuccessResponse<T>;
  return body.data;
}

/**
 * Single-call file upload — sends the file as multipart to cubone, which
 * proxies it into the intent sandbox in S3 and returns the file ref shape
 * we'll later embed in `createIntent`'s `files` array.
 *
 * Replaces the older two-step flow (mint a presigned URL, then PUT to S3
 * directly) because the widget's deploy origin doesn't have CORS rules on
 * the S3 bucket. See README "Upload path" section for the trade-off.
 */
export async function uploadIntentFile(file: File): Promise<IntentFileRef> {
  if (MOCK_API) {
    await new Promise((r) => setTimeout(r, 400));
    return {
      filename: file.name,
      s3_key: `intent_sandbox/mock-${Date.now()}/${file.name}`,
      size: file.size,
      content_type: file.type || "application/octet-stream",
    };
  }
  const form = new FormData();
  form.append("file", file, file.name);
  return request<IntentFileRef>(`/onboard/intent/upload`, {
    method: "POST",
    body: form,
  });
}

export async function createIntent(
  payload: CreateIntentRequest,
): Promise<CreateIntentResponse> {
  if (MOCK_API) {
    await new Promise((r) => setTimeout(r, 250));
    return {
      intent_token: `mock_${Math.random().toString(36).slice(2, 10)}`,
      expires_at: new Date(Date.now() + 48 * 3600 * 1000).toISOString(),
    };
  }
  return request<CreateIntentResponse>(`/onboard/intent`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
